import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProductTour } from '@/components/tutorial/ProductTourContext';
import { useFeatures } from '@/contexts/FeaturesContext';
import { pathMatchesRoute, shouldAdvanceWaitStep, stepWaitsForPath, visibleStepMeta } from '@/tutorials/tours';
import { holeFromRect, placeTourCard, stepUnlocksTarget } from '@/tutorials/tourPlacement';

function isOnScreen(el) {
  const box = el.getBoundingClientRect();
  return box.width > 2 && box.height > 2;
}

function findTarget(step) {
  if (!step) return null;
  const primary = [...document.querySelectorAll(step.selector)];
  const alt = step.altSelector ? [...document.querySelectorAll(step.altSelector)] : [];
  return [...primary, ...alt].find(isOnScreen) || null;
}

function Arrow({ placement }) {
  const base = 'absolute w-0 h-0 border-8';
  if (placement === 'bottom') {
    return (
      <span
        className={`${base} -top-4 left-8 border-x-transparent border-t-transparent border-b-card`}
        aria-hidden="true"
      />
    );
  }
  if (placement === 'top') {
    return (
      <span
        className={`${base} -bottom-4 left-8 border-x-transparent border-b-transparent border-t-card`}
        aria-hidden="true"
      />
    );
  }
  if (placement === 'right') {
    return (
      <span
        className={`${base} top-6 -left-4 border-y-transparent border-l-transparent border-r-card`}
        aria-hidden="true"
      />
    );
  }
  if (placement === 'left') {
    return (
      <span
        className={`${base} top-6 -right-4 border-y-transparent border-r-transparent border-l-card`}
        aria-hidden="true"
      />
    );
  }
  return null;
}

function nudgeText(step, features) {
  if (!step?.nudge || features?.loading) return '';
  if (step.nudge === 'docs-pro' && !features?.hasFeature?.('document_attachments')) {
    return 'Keeping papers on the file is on Pro.';
  }
  if (step.nudge === 'unified-view' && !features?.hasFeature?.('unified_view')) {
    return 'Seeing every file on one timeline is on Basic and up.';
  }
  if (step.nudge === 'team-list' && !features?.hasOrganization) {
    return 'If you work with others, a Team plan can share one people list.';
  }
  return '';
}

function extraHint(step) {
  if (!step?.hintDataset || !step?.hintWhenPresent) return '';
  if (typeof document === 'undefined') return '';
  if (document.documentElement?.dataset?.[step.hintDataset] !== '1') return '';
  return step.hintWhenPresent;
}

function chipLabel(tour, step, progress, pathname) {
  const place = `${progress.current} of ${progress.total}`;
  if (stepWaitsForPath(step, pathname)) return `Same tour · open a deal · ${place}`;
  if (shouldAdvanceWaitStep(step, pathname)) return `Same tour · continuing · ${place}`;
  return `${tour.title} · ${place}`;
}

export function ProductTourOverlay() {
  const location = useLocation();
  const navigate = useNavigate();
  const ctx = useProductTour();
  const features = useFeatures();
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const [cardSize, setCardSize] = useState({ w: 352, h: 220 });
  const cardRef = useRef(null);

  const step = ctx?.step;
  const tour = ctx?.tour;
  const waiting = Boolean(step && stepWaitsForPath(step, location.pathname));
  const handingOff = Boolean(step && shouldAdvanceWaitStep(step, location.pathname));
  const unlockTarget = stepUnlocksTarget(step, missing);
  const onStepScreen = Boolean(step && pathMatchesRoute(location.pathname, step.route));
  const keepPageVisible = Boolean(step?.keepPageVisible);
  const extra = nudgeText(step, features);
  const hint = extraHint(step);
  const progress = tour
    ? visibleStepMeta(tour, ctx?.stepIndex ?? 0, location.pathname, '', ctx?.active)
    : { current: 1, total: 1, isFirst: true, isLast: true };
  const lastStep = progress.isLast;

  useLayoutEffect(() => {
    if (!step || !onStepScreen) {
      setRect(null);
      setMissing(false);
      return undefined;
    }

    let tries = 0;
    const tick = () => {
      const el = findTarget(step);
      if (el) {
        const next = el.getBoundingClientRect();
        setRect({
          top: next.top,
          left: next.left,
          width: next.width,
          height: next.height,
        });
        setMissing(false);
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        return true;
      }
      setRect(null);
      return false;
    };

    tick();
    const id = window.setInterval(() => {
      tries += 1;
      if (tick() || tries > 40) {
        window.clearInterval(id);
        if (!findTarget(step)) setMissing(true);
      }
    }, 120);

    const onMove = () => {
      const el = findTarget(step);
      if (!el) return;
      const next = el.getBoundingClientRect();
      setRect({
        top: next.top,
        left: next.left,
        width: next.width,
        height: next.height,
      });
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [step, onStepScreen, location.pathname, location.search]);

  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const next = node.getBoundingClientRect();
    const w = Math.round(next.width);
    const h = Math.round(next.height);
    setCardSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, [step, rect, missing, onStepScreen, handingOff, waiting]);

  if (!ctx || !step || !tour) return null;

  const hole = onStepScreen ? holeFromRect(rect) : null;
  const placed = placeTourCard({
    hole,
    vw: typeof window === 'undefined' ? 1024 : window.innerWidth,
    vh: typeof window === 'undefined' ? 768 : Math.max(240, window.innerHeight - 64),
    cardW: cardSize.w,
    cardH: cardSize.h,
  });
  const swallow = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="pointer-events-none fixed inset-0 z-[90]" data-testid="product-tour-overlay">
      {onStepScreen && hole && !keepPageVisible ? (
        <>
          <div
            className="pointer-events-auto fixed left-0 right-0 top-0 bg-black/55"
            style={{ height: hole.top }}
            onClick={swallow}
          />
          <div
            className="pointer-events-auto fixed left-0 right-0 bottom-0 bg-black/55"
            style={{ top: hole.top + hole.height }}
            onClick={swallow}
          />
          <div
            className="pointer-events-auto fixed left-0 bg-black/55"
            style={{ top: hole.top, width: hole.left, height: hole.height }}
            onClick={swallow}
          />
          <div
            className="pointer-events-auto fixed right-0 bg-black/55"
            style={{ top: hole.top, left: hole.left + hole.width, height: hole.height }}
            onClick={swallow}
          />
          {!unlockTarget ? (
            <div
              className="pointer-events-auto fixed"
              style={{
                top: hole.top,
                left: hole.left,
                width: hole.width,
                height: hole.height,
              }}
              onClick={swallow}
            />
          ) : null}
        </>
      ) : null}
      {onStepScreen && hole ? (
        <div
          className="pointer-events-none fixed rounded-lg ring-2 ring-primary tour-hole-pulse"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
          }}
        />
      ) : null}

      <div
        className="pointer-events-auto fixed bottom-4 left-1/2 z-[92] flex -translate-x-1/2 items-center gap-3 rounded-full border border-border bg-card px-4 py-2 text-sm shadow-lg"
        data-testid="tour-progress-chip"
      >
        <span className="text-foreground">{chipLabel(tour, step, progress, location.pathname)}</span>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={ctx.stopTour}
        >
          Close
        </button>
      </div>

      {handingOff ? (
        <div
          ref={cardRef}
          className="pointer-events-auto fixed bottom-20 left-1/2 z-[91] w-[min(22rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-border bg-card p-4 shadow-2xl"
          role="status"
        >
          <h2 className="text-lg font-semibold mb-2">Still the same tour</h2>
          <p className="text-sm text-muted-foreground">
            This file is open. Next we look around inside it.
          </p>
        </div>
      ) : onStepScreen ? (
        <div
          ref={cardRef}
          className={
            keepPageVisible
              ? 'pointer-events-auto fixed bottom-20 right-4 z-[91] w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-border bg-card p-4 shadow-2xl'
              : 'pointer-events-auto absolute w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-border bg-card p-4 shadow-2xl'
          }
          style={keepPageVisible ? undefined : { top: placed.top, left: placed.left }}
          role="dialog"
          aria-label={step.title}
        >
          {keepPageVisible ? null : <Arrow placement={placed.placement} />}
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-xs text-muted-foreground">
              {waiting ? 'Same tour. Open a deal to continue.' : `${progress.current} of ${progress.total}`}
            </p>
            <button
              type="button"
              onClick={ctx.stopTour}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close tour"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h2 className="text-lg font-semibold mb-2">{step.title}</h2>
          <p className="text-sm text-muted-foreground mb-3">{step.body}</p>
          {hint ? <p className="text-sm text-muted-foreground mb-3">{hint}</p> : null}
          {extra ? <p className="text-sm text-muted-foreground mb-3">{extra}</p> : null}
          {missing && step.fallback ? (
            <p className="text-sm text-amber-400 mb-3">{step.fallback}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {progress.isFirst ? null : (
              <Button size="sm" variant="ghost" onClick={ctx.prevStep} data-testid="tour-back">
                Back
              </Button>
            )}
            {waiting && missing ? (
              <Button size="sm" onClick={ctx.stopTour} data-testid="tour-next">
                OK
              </Button>
            ) : waiting ? null : (
              <Button size="sm" onClick={ctx.nextStep} data-testid="tour-next">
                {lastStep ? 'Done' : 'Next'}
              </Button>
            )}
          </div>
        </div>
      ) : waiting ? (
        <div
          ref={cardRef}
          className="pointer-events-auto fixed bottom-20 left-1/2 z-[91] w-[min(22rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-border bg-card p-4 shadow-2xl"
          role="dialog"
          aria-label="Continue the tour"
        >
          <h2 className="text-lg font-semibold mb-2">Still the same tour</h2>
          <p className="text-sm text-muted-foreground mb-3">
            Open a deal from the Dashboard to keep going.
          </p>
          <Button size="sm" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      ) : null}
    </div>
  );
}
